import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MessageBox from './MessageBox';
import ConfirmDialog from './ConfirmDialog';
import {
  fetchFeesMeta,
  fetchFeeLines,
  importFeeFiles,
  downloadFeesFromEdupage,
  payFeeLine,
  bulkPayFeeLines,
  exportEdupage,
  markFeesExported,
  resetFeePeriod,
  clearFeePeriod,
  createFeePeriod,
  activateFeePeriod,
  fetchServerConfig,
  createFeeBackup,
  fetchFeeBackups,
  fetchFeeBackupCalendar
} from '../utils/feesApi';
import { printFeeReceipt, captureFeeReceiptPng, copyFeePngBlob } from '../utils/printFees';
import { copyToClipboard } from '../utils/clipboard';
import DateFilterCalendar from './DateFilterCalendar';
import './FeesTab.css';

function todayDisplay() {
  return new Date().toLocaleDateString('en-GB');
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString('en-PK', { maximumFractionDigits: 0 });
}

export default function FeesTab() {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'administrator';

  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [downloadingEdupage, setDownloadingEdupage] = useState(false);
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [lanUrl, setLanUrl] = useState('');
  const [viewMode, setViewMode] = useState('collect'); // collect | backup
  const [backupDate, setBackupDate] = useState('');
  const [backupCalendarItems, setBackupCalendarItems] = useState([]);
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [expandedBackupId, setExpandedBackupId] = useState(null);
  const [backupSearchName, setBackupSearchName] = useState('');
  const [backupSearchClass, setBackupSearchClass] = useState('');
  const [backupSearchType, setBackupSearchType] = useState(''); // '' | print | screenshot
  const [backupSearchStaff, setBackupSearchStaff] = useState('');

  const [name, setName] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [phone, setPhone] = useState('');
  const [unpaidOnly, setUnpaidOnly] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [payModal, setPayModal] = useState(null);
  const [shotModal, setShotModal] = useState(null); // { url, blob }
  const shotImgRef = useRef(null);
  const [exportText, setExportText] = useState('');
  const [lastExportIds, setLastExportIds] = useState([]);

  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [actionLoading, setActionLoading] = useState({ show: false, label: '' });
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const skipAmountBlurRef = useRef(false);

  const fileInputRef = useRef(null);

  const showError = (err) => {
    setMessage({ show: true, text: err.message || 'Something went wrong', type: 'error' });
  };

  const handleConfirmOk = async () => {
    const cb = confirm.onConfirm;
    setConfirm({ show: false, text: '', onConfirm: null });
    if (!cb) return;
    try {
      await cb();
    } catch (err) {
      showError(err);
    }
  };

  const handleConfirmCancel = () => {
    setConfirm({ show: false, text: '', onConfirm: null });
  };

  const loadMeta = useCallback(async () => {
    const data = await fetchFeesMeta();
    setPeriods(data.periods || []);
    setActivePeriod(data.activePeriod);
    setPaymentTypes(data.paymentTypes || []);
  }, []);

  /** Full period list once — filters run offline in the browser. */
  const loadLines = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFeeLines();
      setLines(data.lines || []);
      setSummary(data.summary || null);
      if (data.activePeriod) setActivePeriod(data.activePeriod);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredLines = useMemo(() => {
    const nameQ = name.trim().toLowerCase();
    const classQ = classFilter.trim().toLowerCase();
    const phoneQ = phone.trim().replace(/\D/g, '');

    return lines.filter((line) => {
      if (unpaidOnly && !(Number(line.balance) > 0)) return false;

      if (nameQ) {
        const student = String(line.student_name || '').toLowerCase();
        const roll = String(line.roll_number || '').toLowerCase();
        if (!student.includes(nameQ) && !roll.includes(nameQ)) return false;
      }

      if (classQ) {
        const cls = String(line.class_name || '').toLowerCase();
        if (!cls.includes(classQ)) return false;
      }

      if (phoneQ) {
        const digits = String(line.phone || '').replace(/\D/g, '');
        if (!digits.includes(phoneQ)) return false;
      }

      return true;
    });
  }, [lines, name, classFilter, phone, unpaidOnly]);

  useEffect(() => {
    (async () => {
      try {
        await loadMeta();
        const cfg = await fetchServerConfig();
        if (cfg?.ip) {
          setLanUrl(`http://${cfg.ip}:${cfg.frontendPort || 3000}/fees`);
        }
      } catch {
        // server-config is optional
      }
    })();
  }, [loadMeta]);

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredLines.length && filteredLines.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLines.map((l) => l.id)));
    }
  };

  const handleImport = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    setImporting(true);
    setActionLoading({ show: true, label: 'Importing fee data… clearing old list first' });
    try {
      const data = await importFeeFiles(files);
      setLines(data.lines || []);
      setSummary(data.summary || null);
      setExportText('');
      setLastExportIds([]);
      setSelectedIds(new Set());
      showToast(data.message || 'Import complete — previous data replaced', data.missingPlans?.length ? 'error' : 'success');
    } catch (err) {
      showError(err);
    } finally {
      setImporting(false);
      setActionLoading({ show: false, label: '' });
    }
  };

  const handleDownloadFromEdupage = async () => {
    if (downloadingEdupage || importing) return;
    if (!activePeriod) {
      setMessage({
        show: true,
        text: 'Create or activate a fee period first',
        type: 'error'
      });
      return;
    }

    setDownloadingEdupage(true);
    setActionLoading({
      show: true,
      label: 'Downloading from EduPage in this Chrome… keep the EduPage tab open'
    });
    try {
      const data = await downloadFeesFromEdupage();
      setLines(data.lines || []);
      setSummary(data.summary || null);
      setExportText('');
      setLastExportIds([]);
      setSelectedIds(new Set());
      showToast(
        data.message || 'Downloaded and imported from EduPage',
        data.missingPlans?.length ? 'error' : 'success'
      );
    } catch (err) {
      showError(err);
    } finally {
      setDownloadingEdupage(false);
      setActionLoading({ show: false, label: '' });
    }
  };

  /** Full pay — no confirmation modal; uses default payment type. */
  const payFull = async (line) => {
    try {
      const data = await payFeeLine(line.id, {
        payment_type: paymentTypes[0] || 'Cash',
        full: true
      });
      setSummary(data.summary);
      setLines((prev) => prev.map((row) => (row.id === data.line.id ? data.line : row)));
      showToast('Payment recorded', 'success');
    } catch (err) {
      showError(err);
    }
  };

  /** Partial pay — only open modal when an amount must be entered. */
  const openPartialPayModal = (line) => {
    setPayModal({
      line,
      full: false,
      amount: '',
      payment_type: paymentTypes[0] || 'Cash'
    });
  };

  const submitPartialPay = async () => {
    if (!payModal?.line) return;
    try {
      const data = await payFeeLine(payModal.line.id, {
        payment_type: payModal.payment_type,
        full: false,
        amount: Number(payModal.amount)
      });
      setSummary(data.summary);
      setLines((prev) => prev.map((row) => (row.id === data.line.id ? data.line : row)));
      setPayModal(null);
      showToast('Payment recorded', 'success');
    } catch (err) {
      showError(err);
    }
  };

  const saveAmountInline = async (line, rawValue) => {
    const cleaned = String(rawValue ?? '').replace(/,/g, '').trim();
    const amount = cleaned === '' ? 0 : Number(cleaned);
    const previous = Number(line.amount) || 0;
    if (!Number.isFinite(amount) || amount < 0) {
      showToast('Enter a valid amount', 'error');
      setLines((prev) => prev.map((row) => (row.id === line.id ? { ...row } : row)));
      return;
    }
    if (amount === previous) return;

    try {
      const data = await payFeeLine(line.id, {
        amount,
        payment_type: line.payment_type || paymentTypes[0] || 'Cash',
        full: false
      });
      setSummary(data.summary);
      setLines((prev) => prev.map((row) => (row.id === data.line.id ? data.line : row)));
      showToast(`Amount updated: ${formatMoney(amount)}`, 'success');
    } catch (err) {
      showError(err);
      setLines((prev) => prev.map((row) => (row.id === line.id ? { ...row } : row)));
    }
  };

  const focusAmountByIndex = (index) => {
    if (index < 0 || index >= filteredLines.length) return false;
    const el = document.querySelector(`input.fees-amount-input[data-amount-index="${index}"]`);
    if (!el) return false;
    el.focus();
    el.select();
    return true;
  };

  const handleAmountKeyDown = async (e, index, line) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.currentTarget.blur();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

    e.preventDefault();
    const nextIndex = e.key === 'ArrowDown' ? index + 1 : index - 1;
    if (nextIndex < 0 || nextIndex >= filteredLines.length) return;

    const value = e.currentTarget.value;
    skipAmountBlurRef.current = true;
    await saveAmountInline(line, value);

    setTimeout(() => {
      if (!focusAmountByIndex(nextIndex)) {
        setTimeout(() => focusAmountByIndex(nextIndex), 50);
      }
    }, 30);
  };

  const handleAmountBlur = (line, rawValue) => {
    if (skipAmountBlurRef.current) {
      skipAmountBlurRef.current = false;
      return;
    }
    saveAmountInline(line, rawValue);
  };

  const handleBulkPay = async () => {
    if (!selectedIds.size) {
      showToast('Select rows first', 'error');
      return;
    }
    try {
      const data = await bulkPayFeeLines(
        Array.from(selectedIds),
        paymentTypes[0] || 'Cash'
      );
      setSummary(data.summary);
      await loadLines();
      setSelectedIds(new Set());
      showToast(`Paid ${data.updated?.length || 0} row(s)`, 'success');
    } catch (err) {
      showError(err);
    }
  };

  const handleCopyEdupage = async () => {
    try {
      const data = await exportEdupage();
      if (!data.text || !data.count) {
        setExportText('');
        setLastExportIds([]);
        showToast('No rows of yours ready for EduPage', 'error');
        return;
      }

      const ids = Array.isArray(data.ids) ? data.ids : (data.lines || []).map((l) => l.id);
      const ok = await copyToClipboard(data.text);
      if (!ok) {
        setExportText(data.text);
        setLastExportIds(ids);
        showToast('Copy failed — use the text box below, then Mark Done', 'error');
        return;
      }

      // Clipboard has the paste text. Mark those rows Done so the pending
      // EduPage total clears without a second click.
      setLastExportIds(ids);
      try {
        const marked = await markFeesExported(ids.length ? ids : undefined);
        setLines(marked.lines || []);
        setSummary(marked.summary || null);
        setExportText('');
        setLastExportIds([]);
        showToast(
          `Copied your ${data.count} row(s) and marked them Done — paste in EduPage`,
          'success'
        );
      } catch {
        setExportText(data.text);
        showToast('Copied — Mark Done failed, click Mark Done to clear them', 'error');
      }
    } catch (err) {
      showError(err);
    }
  };

  const handleMarkDone = async () => {
    try {
      const data = await markFeesExported(lastExportIds.length ? lastExportIds : undefined);
      setLines(data.lines || []);
      setSummary(data.summary || null);
      setExportText('');
      setLastExportIds([]);
      showToast('Marked your rows as Done', 'success');
    } catch (err) {
      showError(err);
    }
  };

  const handleReset = () => {
    setConfirm({
      show: true,
      text: 'Reset will carry unpaid balances forward and clear payment dates/types. Continue?',
      onConfirm: async () => {
        setActionLoading({ show: true, label: 'Resetting fee data…' });
        try {
          const data = await resetFeePeriod();
          setLines(data.lines || []);
          setSummary(data.summary || null);
          showToast(data.message || 'Reset complete', 'success');
        } catch (err) {
          showError(err);
        } finally {
          setActionLoading({ show: false, label: '' });
        }
      }
    });
  };

  const handleClear = () => {
    setConfirm({
      show: true,
      text: 'Clear ALL fee lines for this period? This cannot be undone.',
      confirmLabel: 'Empty data',
      onConfirm: async () => {
        setActionLoading({ show: true, label: 'Emptying fee data…' });
        try {
          const data = await clearFeePeriod();
          setLines([]);
          setSummary(data.summary || null);
          setSelectedIds(new Set());
          showToast('All fee data cleared', 'success');
        } catch (err) {
          showError(err);
        } finally {
          setActionLoading({ show: false, label: '' });
        }
      }
    });
  };

  const handleNewPeriod = async () => {
    const label = prompt('Fee period label (e.g. Sep 2027):');
    if (!label?.trim()) return;
    try {
      const data = await createFeePeriod(label.trim(), true);
      setPeriods(data.periods || []);
      await loadMeta();
      await loadLines();
      showToast('Period created', 'success');
    } catch (err) {
      showError(err);
    }
  };

  const handleActivatePeriod = async (id) => {
    try {
      const data = await activateFeePeriod(id);
      setPeriods(data.periods || []);
      await loadMeta();
      await loadLines();
    } catch (err) {
      showError(err);
    }
  };

  const showToast = (text, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ text, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  };

  const handleCopyPhone = async (phoneValue) => {
    const text = String(phoneValue || '').trim();
    if (!text) return;
    const ok = await copyToClipboard(text);
    showToast(ok ? `Copied ${text}` : 'Could not copy phone number', ok ? 'success' : 'error');
  };

  const clearFilters = () => {
    setName('');
    setClassFilter('');
    setPhone('');
    setUnpaidOnly(false);
  };

  const loadBackupCalendar = useCallback(async () => {
    try {
      const data = await fetchFeeBackupCalendar();
      setBackupCalendarItems(data.items || []);
    } catch (err) {
      console.warn('fee backup calendar:', err);
    }
  }, []);

  const loadBackupsForDate = useCallback(async (date) => {
    if (!date) {
      setBackups([]);
      return;
    }
    setBackupLoading(true);
    try {
      const data = await fetchFeeBackups(date);
      setBackups(data.backups || []);
    } catch (err) {
      showError(err);
    } finally {
      setBackupLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode !== 'backup') return;
    loadBackupCalendar();
    if (!backupDate) {
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      setBackupDate(`${y}-${m}-${d}`);
    }
  }, [viewMode, loadBackupCalendar, backupDate]);

  useEffect(() => {
    if (viewMode !== 'backup' || !backupDate) return;
    loadBackupsForDate(backupDate);
  }, [viewMode, backupDate, loadBackupsForDate]);

  const getPrintRows = () =>
    selectedIds.size > 0
      ? filteredLines.filter((line) => selectedIds.has(line.id))
      : filteredLines;

  const saveBackupRecord = async (actionType, rows) => {
    try {
      await createFeeBackup(actionType, rows);
      if (viewMode === 'backup') {
        await loadBackupCalendar();
        if (backupDate) await loadBackupsForDate(backupDate);
      }
    } catch (err) {
      console.warn('fee backup save failed:', err);
    }
  };

  const handlePrint = async () => {
    const rows = getPrintRows();

    if (!rows.length) {
      showToast('Nothing to print — select rows or filter a student first', 'error');
      return;
    }

    try {
      printFeeReceipt(rows);
      await saveBackupRecord('print', rows);
      showToast('Print started — saved to Backup', 'success');
    } catch (err) {
      showError(err);
    }
  };

  const closeShotModal = () => {
    setShotModal((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const handleScreenshot = async () => {
    const rows = getPrintRows();
    if (!rows.length) {
      showToast('Nothing to screenshot — select rows or filter a student first', 'error');
      return;
    }

    setActionLoading({ show: true, label: 'Capturing screenshot for WhatsApp…' });
    try {
      const captured = await captureFeeReceiptPng(rows);
      await saveBackupRecord('screenshot', rows);
      const url = URL.createObjectURL(captured.blob);
      setShotModal({ url, blob: captured.blob });
    } catch (err) {
      showError(err);
    } finally {
      setActionLoading({ show: false, label: '' });
    }
  };

  const handleCopyShot = async () => {
    if (!shotModal?.blob) return;
    try {
      await copyFeePngBlob(shotModal.blob, shotImgRef.current);
      showToast('Image copied — paste in WhatsApp (Ctrl+V)', 'success');
      closeShotModal();
    } catch (err) {
      showError(
        new Error(
          err.message ||
            'Could not copy the image. Use Download, then share the PNG in WhatsApp.'
        )
      );
    }
  };

  const handleDownloadShot = () => {
    if (!shotModal?.blob) return;
    const a = document.createElement('a');
    a.href = shotModal.url;
    a.download = `fee-receipt-${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
    showToast('PNG downloaded — share it in WhatsApp', 'success');
  };

  const receivedToday = useMemo(() => formatMoney(summary?.total_received), [summary]);
  const forEdupage = useMemo(() => formatMoney(summary?.for_edupage), [summary]);

  const filteredBackups = useMemo(() => {
    const nameQ = backupSearchName.trim().toLowerCase();
    const classQ = backupSearchClass.trim().toLowerCase();
    const staffQ = backupSearchStaff.trim().toLowerCase();
    const typeQ = backupSearchType;

    return (backups || []).filter((b) => {
      if (typeQ && b.action_type !== typeQ) return false;
      if (staffQ) {
        const staff = String(b.created_by_name || '').toLowerCase();
        if (!staff.includes(staffQ)) return false;
      }
      if (!nameQ && !classQ) return true;

      const lines = Array.isArray(b.lines) ? b.lines : [];
      return lines.some((line) => {
        const student = String(line.student_name || '').toLowerCase();
        const roll = String(line.roll_number || '').toLowerCase();
        const cls = String(line.class_name || '').toLowerCase();
        const nameOk = !nameQ || student.includes(nameQ) || roll.includes(nameQ);
        const classOk = !classQ || cls.includes(classQ);
        return nameOk && classOk;
      });
    });
  }, [backups, backupSearchName, backupSearchClass, backupSearchType, backupSearchStaff]);

  const clearBackupFilters = () => {
    setBackupSearchName('');
    setBackupSearchClass('');
    setBackupSearchType('');
    setBackupSearchStaff('');
  };

  const grandTotal = useMemo(() => {
    let fees = 0;
    let arrear = 0;
    let payable = 0;
    let amount = 0;
    let balance = 0;
    for (const line of filteredLines) {
      fees += Number(line.fees) || 0;
      arrear += Number(line.arrear) || 0;
      payable += Number(line.payable) || 0;
      amount += Number(line.amount) || 0;
      balance += Number(line.balance) || 0;
    }
    return { fees, arrear, payable, amount, balance, count: filteredLines.length };
  }, [filteredLines]);

  return (
    <div className="fees-tab">
      <div className="fees-header">
        <h1>Fees</h1>
        <p className="fees-header-period">
          Active period: <strong>{activePeriod?.label || 'None'}</strong> · {todayDisplay()}
        </p>
        {lanUrl && (
          <div className="fees-lan-hint">
            Staff on Wi‑Fi: bookmark this URL (IP may change):{' '}
            <code>{lanUrl}</code>
          </div>
        )}
        <div className="fees-view-tabs">
          <button
            type="button"
            className={`fees-view-tab ${viewMode === 'collect' ? 'active' : ''}`}
            onClick={() => setViewMode('collect')}
          >
            Collect
          </button>
          <button
            type="button"
            className={`fees-view-tab ${viewMode === 'backup' ? 'active' : ''}`}
            onClick={() => setViewMode('backup')}
          >
            Backup
          </button>
        </div>
      </div>

      {viewMode === 'backup' ? (
        <div className="fees-backup-wrap">
          <div className="fees-toolbar fees-backup-toolbar">
            <div className="fees-field">
              <label>Name</label>
              <input
                value={backupSearchName}
                onChange={(e) => setBackupSearchName(e.target.value)}
                placeholder="Student name / roll"
              />
            </div>
            <div className="fees-field">
              <label>Class</label>
              <input
                value={backupSearchClass}
                onChange={(e) => setBackupSearchClass(e.target.value)}
                placeholder="Class"
              />
            </div>
            <div className="fees-field">
              <label>Type</label>
              <select
                value={backupSearchType}
                onChange={(e) => setBackupSearchType(e.target.value)}
              >
                <option value="">All</option>
                <option value="print">Print</option>
                <option value="screenshot">Screenshot</option>
              </select>
            </div>
            <div className="fees-field">
              <label>Staff</label>
              <input
                value={backupSearchStaff}
                onChange={(e) => setBackupSearchStaff(e.target.value)}
                placeholder="Printed / captured by"
              />
            </div>
            <button
              type="button"
              className="fees-btn fees-btn-secondary"
              onClick={clearBackupFilters}
              title="Clear backup search filters"
            >
              Clear
            </button>
          </div>

          <div className="fees-backup-layout">
          <div className="fees-backup-calendar">
            <DateFilterCalendar
              items={backupCalendarItems}
              dateField="backup_date"
              selectedDate={backupDate}
              onDateChange={(d) => {
                setBackupDate(d);
                setExpandedBackupId(null);
              }}
              itemLabelSingular="backup"
              itemLabelPlural="backups"
              title="Print / Screenshot days"
              windowDays={30}
              columns={5}
            />
          </div>
          <div className="fees-backup-list">
            {!backupDate ? (
              <div className="fees-empty">Select a day on the calendar</div>
            ) : backupLoading ? (
              <div className="fees-loading">Loading backups…</div>
            ) : backups.length === 0 ? (
              <div className="fees-empty">No print/screenshot backups for this day</div>
            ) : filteredBackups.length === 0 ? (
              <div className="fees-empty">No backups match your search</div>
            ) : (
              filteredBackups.map((b) => {
                const open = expandedBackupId === b.id;
                const when = b.created_at
                  ? new Date(b.created_at).toLocaleString('en-GB')
                  : '';
                const first = (b.lines && b.lines[0]) || null;
                const firstName = first
                  ? [
                      first.roll_number
                        ? `(${String(first.roll_number).replace(/^["'\s]+|["'\s]+$/g, '')})`
                        : '',
                      String(first.student_name || '').replace(/^["'\s]+|["'\s]+$/g, '').trim()
                    ]
                      .filter(Boolean)
                      .join(' ')
                  : '';
                return (
                  <div key={b.id} className="fees-backup-card">
                    <button
                      type="button"
                      className="fees-backup-card-head"
                      onClick={() => setExpandedBackupId(open ? null : b.id)}
                    >
                      <span className={`fees-backup-type fees-backup-type-${b.action_type}`}>
                        {b.action_type === 'screenshot' ? 'Screenshot' : 'Print'}
                      </span>
                      <span className="fees-backup-first-name" title={firstName || undefined}>
                        {firstName || '—'}
                      </span>
                      <span>{when}</span>
                      <span>{b.row_count} row(s)</span>
                      <span>Total: {formatMoney(b.total_amount)}</span>
                      <span>{b.created_by_name || '—'}</span>
                      <span>{open ? '▼' : '▶'}</span>
                    </button>
                    {open && (
                      <div className="fees-backup-card-body">
                        <table className="fees-table fees-backup-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Class</th>
                              <th>Plan</th>
                              <th>Payable</th>
                              <th>Amount</th>
                              <th>Balance</th>
                              <th>Date</th>
                              <th>Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(b.lines || []).map((line, idx) => (
                              <tr key={`${b.id}-${line.id || idx}`}>
                                <td>
                                  {line.roll_number ? `(${line.roll_number})` : ''}
                                  {line.student_name}
                                </td>
                                <td>{line.class_name}</td>
                                <td>{line.payment_plan}</td>
                                <td>{formatMoney(line.payable)}</td>
                                <td>{formatMoney(line.amount)}</td>
                                <td>{formatMoney(line.balance)}</td>
                                <td>{line.payment_date ? String(line.payment_date).slice(0, 10) : ''}</td>
                                <td>{line.payment_type || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          </div>
        </div>
      ) : (
      <>
      <div className="fees-toolbar">
        <div className="fees-field">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Search name" />
        </div>
        <div className="fees-field">
          <label>Class</label>
          <input value={classFilter} onChange={(e) => setClassFilter(e.target.value)} placeholder="Class" />
        </div>
        <div className="fees-field">
          <label>Phone</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
        </div>
        <div className="fees-field">
          <label>Period</label>
          <select
            value={activePeriod?.id || ''}
            onChange={(e) => handleActivatePeriod(Number(e.target.value))}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}{p.is_active ? ' (active)' : ''}
              </option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
          <input type="checkbox" checked={unpaidOnly} onChange={(e) => setUnpaidOnly(e.target.checked)} />
          Unpaid only
        </label>
        <button
          type="button"
          className="fees-btn fees-btn-secondary"
          onClick={clearFilters}
          title="Clear name, class, and phone search filters"
        >
          Clear filter
        </button>
        <button
          type="button"
          className="fees-btn fees-btn-secondary"
          onClick={handleNewPeriod}
          title="Create a new fee period (e.g. next month) and make it active"
        >
          New period
        </button>
      </div>

      <div className="fees-command-row">
        <div className="fees-total-card" title="Amount you collected in the active period (not other staff)">
          <strong>{receivedToday}</strong>
          <span>total received (yours)</span>
        </div>
        <div className="fees-total-card alt" title="Your paid amounts still waiting to be copied into EduPage (not other staff)">
          <strong>{forEdupage}</strong>
          <span>EduPage (yours)</span>
        </div>
        <button
          type="button"
          className="fees-btn fees-btn-green"
          onClick={handleBulkPay}
          title="Mark all checked rows as fully paid"
        >
          Pay selected
        </button>
        <button
          type="button"
          className="fees-btn fees-btn-blue"
          onClick={handleCopyEdupage}
          title="Copy the payments you collected and mark them Done"
        >
          Copy for EduPage
        </button>
        <button
          type="button"
          className="fees-btn fees-btn-blue"
          onClick={handleMarkDone}
          title="Mark only your copied payments as Done in EduPage"
        >
          Mark Done
        </button>
        <button
          type="button"
          className="fees-btn fees-btn-purple"
          onClick={handlePrint}
          title="Print selected or filtered rows (max 13 per page)"
        >
          Print
        </button>
        <button
          type="button"
          className="fees-btn fees-btn-orange"
          onClick={handleScreenshot}
          title="Copy print document as image — paste into WhatsApp"
        >
          Screenshot
        </button>
        <button
          type="button"
          className="fees-btn fees-btn-secondary"
          onClick={handleReset}
          title="Carry unpaid balance forward and clear payment date, type, and status for the next collection"
        >
          Reset
        </button>

        <div className="fees-command-major">
          <button
            type="button"
            className="fees-btn fees-btn-green"
            disabled={downloadingEdupage || importing || !activePeriod}
            onClick={handleDownloadFromEdupage}
            title="Opens EduPage in this Chrome via the Fast Grader → EduPage extension, exports 7 fee files, then imports them"

          >
            {downloadingEdupage ? 'Downloading…' : 'Download from EduPage'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="fees-import-input"
            accept=".xlsx,.xls"
            multiple
            onChange={handleImport}
          />
          <button
            type="button"
            className="fees-btn fees-btn-danger"
            disabled={importing || downloadingEdupage}
            onClick={() => fileInputRef.current?.click()}
            title="Delete all current fee rows, then import selected EduPage Excel files"
          >
            {importing ? 'Importing…' : 'Import data'}
          </button>
          {isAdmin && (
            <button
              type="button"
              className="fees-btn fees-btn-danger"
              onClick={handleClear}
              title="Admin only: delete all fee rows for this period"
            >
              Empty data
            </button>
          )}
        </div>
      </div>

      {exportText && (
        <div className="fees-export-preview" title="Last EduPage export preview">
          {exportText.slice(0, 2000)}
          {exportText.length > 2000 ? '…' : ''}
        </div>
      )}

      <div className="fees-table-wrap">
        {loading ? (
          <div className="fees-loading">Loading…</div>
        ) : lines.length === 0 ? (
          <div className="fees-empty">
            No fee rows. Use Download from EduPage, or Import data (Excel files from Downloads).
          </div>
        ) : filteredLines.length === 0 ? (
          <div className="fees-empty">
            No rows match the current filters.
          </div>
        ) : (
          <table className="fees-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={filteredLines.length > 0 && selectedIds.size === filteredLines.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Class</th>
                <th>Plan</th>
                <th>Fees</th>
                <th>Arrear</th>
                <th>Payable</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Date</th>
                <th>Type</th>
                <th>Status</th>
                <th>Collected by</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLines.map((line, rowIndex) => (
                <tr
                  key={line.id}
                  className={`${selectedIds.has(line.id) ? 'selected' : ''} ${line.entry_status === 'Done' ? 'done' : ''}`}
                  onClick={() => toggleSelect(line.id)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(line.id)}
                      onChange={() => toggleSelect(line.id)}
                    />
                  </td>
                  <td>
                    {line.roll_number ? `(${line.roll_number})` : ''}{line.student_name}
                  </td>
                  <td>{line.class_name}</td>
                  <td>{line.payment_plan}</td>
                  <td>{formatMoney(line.fees)}</td>
                  <td>{formatMoney(line.arrear)}</td>
                  <td>{formatMoney(line.payable)}</td>
                  <td
                    className="fees-no-row-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      className="fees-amount-input"
                      data-amount-index={rowIndex}
                      title="Edit paid amount — ↓ next row, ↑ previous row"
                      key={`${line.id}-${line.amount}-${line.balance}-${line.payment_date || ''}`}
                      defaultValue={Number(line.amount) ? String(Number(line.amount)) : ''}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => handleAmountKeyDown(e, rowIndex, line)}
                      onBlur={(e) => handleAmountBlur(line, e.target.value)}
                    />
                  </td>
                  <td>{formatMoney(line.balance)}</td>
                  <td>{line.payment_date ? String(line.payment_date).slice(0, 10) : ''}</td>
                  <td>{line.payment_type || ''}</td>
                  <td>{line.entry_status || ''}</td>
                  <td>{line.collected_by_name || ''}</td>
                  <td
                    className="fees-no-row-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {line.phone ? (
                      <span className="fees-phone-list">
                        {String(line.phone)
                          .trim()
                          .split(/\s+/)
                          .filter(Boolean)
                          .map((num) => (
                            <button
                              key={num}
                              type="button"
                              className="fees-phone-copy"
                              title={`Click to copy ${num}`}
                              onClick={() => handleCopyPhone(num)}
                            >
                              {num}
                            </button>
                          ))}
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td
                    className="fees-no-row-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="fees-row-actions">
                      <button
                        type="button"
                        onClick={() => payFull(line)}
                        title="Pay the full payable amount now"
                      >
                        Pay
                      </button>
                      <button
                        type="button"
                        onClick={() => openPartialPayModal(line)}
                        title="Enter a partial payment amount for this row"
                      >
                        Partial
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="fees-grand-total">
                <td colSpan={4}>Grand Total ({grandTotal.count} rows)</td>
                <td>{formatMoney(grandTotal.fees)}</td>
                <td>{formatMoney(grandTotal.arrear)}</td>
                <td>{formatMoney(grandTotal.payable)}</td>
                <td>{formatMoney(grandTotal.amount)}</td>
                <td>{formatMoney(grandTotal.balance)}</td>
                <td colSpan={6} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {payModal && (
        <div className="fees-modal-backdrop" onClick={() => setPayModal(null)}>
          <div className="fees-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Partial payment</h3>
            {payModal.line && (
              <p style={{ fontSize: '0.9rem', margin: '0 0 12px' }}>
                {payModal.line.student_name} — Payable: {formatMoney(payModal.line.payable)}
              </p>
            )}
            <div className="fees-field" style={{ marginBottom: 12 }}>
              <label>Amount</label>
              <input
                type="number"
                min="0"
                autoFocus
                value={payModal.amount}
                onChange={(e) => setPayModal({ ...payModal, amount: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitPartialPay();
                }}
              />
            </div>
            <div className="fees-field">
              <label>Payment type</label>
              <select
                value={payModal.payment_type}
                onChange={(e) => setPayModal({ ...payModal, payment_type: e.target.value })}
              >
                {paymentTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="fees-modal-actions">
              <button
                type="button"
                className="fees-btn fees-btn-secondary"
                onClick={() => setPayModal(null)}
                title="Close without saving payment"
              >
                Cancel
              </button>
              <button
                type="button"
                className="fees-btn fees-btn-green"
                onClick={submitPartialPay}
                title="Save this payment"
              >
                Pay
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}

      {shotModal && (
        <div className="fees-modal-backdrop" onClick={closeShotModal}>
          <div
            className="fees-modal fees-shot-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Screenshot ready</h3>
            <p className="fees-shot-hint">
              Click <strong>Copy</strong>, then paste in WhatsApp.
              {typeof window !== 'undefined' && !window.isSecureContext
                ? ' On Wi‑Fi HTTP, if paste fails use Download and share the PNG file.'
                : ''}
            </p>
            <div className="fees-shot-preview">
              <img
                ref={shotImgRef}
                src={shotModal.url}
                alt=""
                draggable={false}
              />
            </div>
            <div className="fees-modal-actions">
              <button
                type="button"
                className="fees-btn fees-btn-secondary"
                onClick={closeShotModal}
              >
                Close
              </button>
              <button
                type="button"
                className="fees-btn fees-btn-secondary"
                onClick={handleDownloadShot}
                title="Download PNG file"
              >
                Download
              </button>
              <button
                type="button"
                className="fees-btn fees-btn-orange"
                onClick={handleCopyShot}
                title="Copy image to clipboard for WhatsApp"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageBox message={message} onClose={() => setMessage({ show: false, text: '', type: 'info' })} />
      <ConfirmDialog
        confirm={confirm}
        onConfirm={handleConfirmOk}
        onCancel={handleConfirmCancel}
      />

      {actionLoading.show && (
        <div className="fees-action-loading" role="status" aria-live="polite">
          <div className="fees-action-loading-card">
            <p>{actionLoading.label || 'Processing…'}</p>
            <div className="fees-progress-bar">
              <div className="fees-progress-bar-fill" />
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`fees-toast fees-toast-${toast.type === 'error' ? 'error' : 'success'}`}
          role="status"
          aria-live="polite"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
